import type { Props } from 'davaux/jsx-runtime'

/** Named size token accepted by most UI components. */
export type UiSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'
/** HTML tag string or a Davaux function component — accepted by the `component` prop on polymorphic components. */
export type UiComponent = string | ((props: Props) => Promise<string>)
/** Border-radius value — a size token, 'default', a rem number, or any CSS string. */
export type UiRadius = UiSize | 'default' | number | (string & {})
/** CSS color value or theme color key (e.g. 'blue', '#fff'). */
export type UiColor = string
/** Spacing value — a size token, a rem number, or any CSS string. */
export type UiSpacing = UiSize | number | (string & {})
/** Box-shadow value — a size token or any CSS shadow string. */
export type UiShadow = UiSize | (string & {})
/** Elevation level in the surface depth hierarchy (0 = flat, 5 = highest floating). */
export type UiElevation = 0 | 1 | 2 | 3 | 4 | 5
/** Gradient definition with start/end colors and optional angle in degrees. */
export type UiGradient = { from: string; to: string; deg?: number }

/** Plain object mapping CSS property names to string or numeric values. */
export type StyleObject = Record<string, string | number>

const SIZES = new Set<string>(['xs', 'sm', 'md', 'lg', 'xl'])

/** Converts a UiSpacing token or raw value to a CSS spacing string. */
export function spacing(val: UiSpacing | undefined): string | undefined {
  if (val === undefined) return undefined
  if (typeof val === 'number') return `${val}rem`
  if (SIZES.has(val)) return `var(--dv-spacing-${val})`
  return val
}

/** Converts a UiRadius token or raw value to a CSS border-radius string. */
export function radius(val: UiRadius | undefined): string | undefined {
  if (val === undefined) return undefined
  if (val === 'default') return 'var(--dv-radius-default)'
  if (typeof val === 'number') return `${val}rem`
  if (SIZES.has(val as string)) return `var(--dv-radius-${val})`
  return val as string
}

/** Converts a UiShadow token or raw value to a CSS box-shadow string. */
export function shadow(val: UiShadow | undefined): string | undefined {
  if (!val) return undefined
  if (SIZES.has(val)) return `var(--dv-shadow-${val})`
  return val
}

/** Converts a UiSize token, number, or raw string to a CSS font-size value. */
export function fontSize(val: UiSize | number | string | undefined): string | undefined {
  if (val === undefined) return undefined
  if (typeof val === 'number') return `${val}rem`
  if (SIZES.has(val)) return `var(--dv-font-size-${val})`
  return val
}

/** Returns a CSS `light-dark()` value that resolves based on the active color scheme. */
export function lightDark(light: string, dark: string): string {
  return `light-dark(${light}, ${dark})`
}

/** Joins truthy class name arguments into a single space-separated string, or undefined if empty. */
export function cx(...classes: (string | undefined | null | false)[]): string | undefined {
  const result = classes.filter(Boolean).join(' ')
  return result || undefined
}

/** Merges component CSS variables with a user-supplied style prop, handling both object and string forms. */
export function mergeStyle(
  cssVars: StyleObject,
  userStyle: Props['style'],
): Props['style'] | undefined {
  const hasVars = Object.keys(cssVars).length > 0
  if (!hasVars && !userStyle) return undefined
  if (!hasVars) return userStyle
  if (!userStyle) return cssVars
  if (typeof userStyle === 'string') {
    const varStr = Object.entries(cssVars)
      .map(([k, v]) => `${k}:${v}`)
      .join(';')
    return `${varStr};${userStyle}`
  }
  return { ...cssVars, ...userStyle }
}

/**
 * Shared resolver for the `c` and `bg` style props. Handles dot notation,
 * semantic aliases, and raw CSS pass-through. `defaultSuffix` controls what
 * a plain scale name resolves to when no dot-notation spec is provided:
 *   c  prop uses 'light-color' (readable text on tinted backgrounds)
 *   bg prop uses 'light'       (tinted background fill)
 */
function resolveToken(val: string, defaultSuffix: string): string {
  // Dot notation: "{scale}.{shade|variant-token}"
  // e.g. "violet.6", "violet.filled", "violet.light-color", "violet.outline"
  const dot = val.indexOf('.')
  if (dot !== -1) {
    const scale = val.slice(0, dot)
    const spec = val.slice(dot + 1)
    return `var(--dv-color-${scale}-${spec})`
  }
  // Plain lowercase color scale name → defaultSuffix token
  if (/^[a-z-]+$/.test(val)) return `var(--dv-color-${val}-${defaultSuffix})`
  // Raw CSS value — pass through as-is
  return val
}

/**
 * Resolves a value for the `c` style prop to a CSS color string.
 *
 * Semantic aliases:
 *   "text" | "dimmed" | "bright" | "border"                — surface tokens
 *   "white" | "black"                                       — absolute palette
 *   "primary" | "error" | "success" | "warning" | "info"   — semantic tokens
 *
 * Scale resolution (plain name or dot notation):
 *   "violet"            → var(--dv-color-violet-light-color)   (default)
 *   "violet.6"          → var(--dv-color-violet-6)             (specific shade)
 *   "violet.filled"     → var(--dv-color-violet-filled)        (filled bg color as text)
 *   "violet.filled-color" → var(--dv-color-violet-filled-color)
 *   "violet.light-color"  → var(--dv-color-violet-light-color) (explicit default)
 *   "violet.outline"    → var(--dv-color-violet-outline)       (matches border color)
 *
 * Raw CSS: "#hex" | "rgb()" | "oklch()" | "var(--…)" — passed through as-is.
 */
export function resolveColor(c: string | undefined): string | undefined {
  if (c === undefined) return undefined
  switch (c) {
    case 'text':
      return 'var(--dv-color-text)'
    case 'dimmed':
      return 'var(--dv-color-dimmed)'
    case 'bright':
      return 'var(--dv-color-bright)'
    case 'border':
      return 'var(--dv-color-border)'
    case 'white':
      return 'var(--dv-color-white)'
    case 'black':
      return 'var(--dv-color-black)'
    case 'primary':
      return 'var(--dv-color-primary-light-color)'
    case 'error':
      return 'var(--dv-color-error)'
    case 'success':
      return 'var(--dv-color-success)'
    case 'warning':
      return 'var(--dv-color-warning)'
    case 'info':
      return 'var(--dv-color-info)'
  }
  return resolveToken(c, 'light-color')
}

/**
 * Resolves a value for the `bg` style prop to a CSS color string.
 *
 * Semantic aliases:
 *   "body" | "surface"                                      — page/panel backgrounds
 *   "text" | "dimmed" | "bright" | "border"                — surface tokens
 *   "white" | "black"                                       — absolute palette
 *   "primary" | "error" | "success" | "warning" | "info"   — semantic tokens (→ filled)
 *
 * Scale resolution (plain name or dot notation):
 *   "violet"              → var(--dv-color-violet-light)    (tinted bg, default)
 *   "violet.6"            → var(--dv-color-violet-6)        (specific shade)
 *   "violet.filled"       → var(--dv-color-violet-filled)   (solid filled background)
 *   "violet.light"        → var(--dv-color-violet-light)    (tinted background, explicit)
 *   "violet.light-hover"  → var(--dv-color-violet-light-hover)
 *
 * Raw CSS: "#hex" | "rgb()" | "oklch()" | "var(--…)" — passed through as-is.
 */
export function resolveBackground(bg: string | undefined): string | undefined {
  if (bg === undefined) return undefined
  switch (bg) {
    case 'body':
      return 'var(--dv-color-body)'
    case 'surface':
      return 'var(--dv-color-surface)'
    case 'text':
      return 'var(--dv-color-text)'
    case 'dimmed':
      return 'var(--dv-color-dimmed)'
    case 'bright':
      return 'var(--dv-color-bright)'
    case 'border':
      return 'var(--dv-color-border)'
    case 'white':
      return 'var(--dv-color-white)'
    case 'black':
      return 'var(--dv-color-black)'
    case 'primary':
      return 'var(--dv-color-primary-filled)'
    case 'error':
      return 'var(--dv-color-error)'
    case 'success':
      return 'var(--dv-color-success)'
    case 'warning':
      return 'var(--dv-color-warning)'
    case 'info':
      return 'var(--dv-color-info)'
  }
  return resolveToken(bg, 'light')
}

/** All Mantine-compatible style props accepted by every UI component. */
export interface StyleProps {
  class?: string
  style?: Props['style']
  m?: UiSpacing
  mt?: UiSpacing
  mb?: UiSpacing
  ml?: UiSpacing
  mr?: UiSpacing
  mx?: UiSpacing
  my?: UiSpacing
  ms?: UiSpacing
  me?: UiSpacing
  mis?: UiSpacing
  mie?: UiSpacing
  p?: UiSpacing
  pt?: UiSpacing
  pb?: UiSpacing
  pl?: UiSpacing
  pr?: UiSpacing
  px?: UiSpacing
  py?: UiSpacing
  ps?: UiSpacing
  pe?: UiSpacing
  pis?: UiSpacing
  pie?: UiSpacing
  c?: string
  bg?: string
  bd?: string
  ff?: string
  fz?: UiSize | number | string
  fw?: number | string
  lh?: string | number
  lts?: string | number
  ta?: 'left' | 'center' | 'right' | 'justify'
  fs?: string
  tt?: 'uppercase' | 'lowercase' | 'capitalize' | 'none'
  td?: string
  w?: string | number
  h?: string | number
  miw?: string | number
  mih?: string | number
  maw?: string | number
  mah?: string | number
  opacity?: number
  bdrs?: UiRadius
  bgsz?: string
  bgp?: string
  bgr?: string
  bga?: string
  pos?: string
  top?: string | number
  left?: string | number
  bottom?: string | number
  right?: string | number
  inset?: string | number
  display?: string
  flex?: string | number
}

/** Converts a partial set of style props to an inline CSS `StyleObject`. */
export function applyStyleProps(props: Partial<StyleProps>): StyleObject {
  const s: StyleObject = {}
  const sp = (v: UiSpacing) => spacing(v) ?? ''
  const dim = (v: string | number) => (typeof v === 'number' ? `${v}px` : v)

  const {
    m,
    mt,
    mb,
    ml,
    mr,
    mx,
    my,
    ms,
    me,
    mis,
    mie,
    p,
    pt,
    pb,
    pl,
    pr,
    px,
    py,
    ps,
    pe,
    pis,
    pie,
    c,
    bg,
    bd,
    ff,
    fz,
    fw,
    lh,
    lts,
    ta,
    fs,
    tt,
    td,
    w,
    h,
    miw,
    mih,
    maw,
    mah,
    opacity,
    bdrs,
    bgsz,
    bgp,
    bgr,
    bga,
    pos,
    top,
    left,
    bottom,
    right,
    inset,
    display,
    flex,
  } = props

  if (m !== undefined) s.margin = sp(m)
  if (mt !== undefined) s['margin-top'] = sp(mt)
  if (mb !== undefined) s['margin-bottom'] = sp(mb)
  if (ml !== undefined) s['margin-left'] = sp(ml)
  if (mr !== undefined) s['margin-right'] = sp(mr)
  if (mx !== undefined) {
    s['margin-left'] = sp(mx)
    s['margin-right'] = sp(mx)
  }
  if (my !== undefined) {
    s['margin-top'] = sp(my)
    s['margin-bottom'] = sp(my)
  }
  if (ms !== undefined) s['margin-inline-start'] = sp(ms)
  if (me !== undefined) s['margin-inline-end'] = sp(me)
  if (mis !== undefined) s['margin-inline-start'] = sp(mis)
  if (mie !== undefined) s['margin-inline-end'] = sp(mie)

  if (p !== undefined) s.padding = sp(p)
  if (pt !== undefined) s['padding-top'] = sp(pt)
  if (pb !== undefined) s['padding-bottom'] = sp(pb)
  if (pl !== undefined) s['padding-left'] = sp(pl)
  if (pr !== undefined) s['padding-right'] = sp(pr)
  if (px !== undefined) {
    s['padding-left'] = sp(px)
    s['padding-right'] = sp(px)
  }
  if (py !== undefined) {
    s['padding-top'] = sp(py)
    s['padding-bottom'] = sp(py)
  }
  if (ps !== undefined) s['padding-inline-start'] = sp(ps)
  if (pe !== undefined) s['padding-inline-end'] = sp(pe)
  if (pis !== undefined) s['padding-inline-start'] = sp(pis)
  if (pie !== undefined) s['padding-inline-end'] = sp(pie)

  if (c !== undefined) s.color = resolveColor(c) ?? c
  if (bg !== undefined) s.background = resolveBackground(bg) ?? bg
  if (bd !== undefined) s.border = bd

  if (ff !== undefined) s['font-family'] = ff
  if (fz !== undefined) s['font-size'] = fontSize(fz) ?? ''
  if (fw !== undefined) s['font-weight'] = fw
  if (lh !== undefined) s['line-height'] = lh
  if (lts !== undefined) s['letter-spacing'] = typeof lts === 'number' ? `${lts}rem` : lts
  if (ta !== undefined) s['text-align'] = ta
  if (fs !== undefined) s['font-style'] = fs
  if (tt !== undefined) s['text-transform'] = tt
  if (td !== undefined) s['text-decoration'] = td

  if (w !== undefined) s.width = dim(w)
  if (h !== undefined) s.height = dim(h)
  if (miw !== undefined) s['min-width'] = dim(miw)
  if (mih !== undefined) s['min-height'] = dim(mih)
  if (maw !== undefined) s['max-width'] = dim(maw)
  if (mah !== undefined) s['max-height'] = dim(mah)

  if (opacity !== undefined) s.opacity = opacity
  if (bdrs !== undefined) s['border-radius'] = radius(bdrs) ?? ''
  if (bgsz !== undefined) s['background-size'] = bgsz
  if (bgp !== undefined) s['background-position'] = bgp
  if (bgr !== undefined) s['background-repeat'] = bgr
  if (bga !== undefined) s['background-attachment'] = bga

  if (pos !== undefined) s.position = pos
  if (top !== undefined) s.top = dim(top)
  if (left !== undefined) s.left = dim(left)
  if (bottom !== undefined) s.bottom = dim(bottom)
  if (right !== undefined) s.right = dim(right)
  if (inset !== undefined) s.inset = dim(inset)
  if (display !== undefined) s.display = display
  if (flex !== undefined) s.flex = flex

  return s
}

const STYLE_PROP_KEYS: ReadonlySet<string> = new Set([
  'm',
  'mt',
  'mb',
  'ml',
  'mr',
  'mx',
  'my',
  'ms',
  'me',
  'mis',
  'mie',
  'p',
  'pt',
  'pb',
  'pl',
  'pr',
  'px',
  'py',
  'ps',
  'pe',
  'pis',
  'pie',
  'c',
  'bg',
  'bd',
  'ff',
  'fz',
  'fw',
  'lh',
  'lts',
  'ta',
  'fs',
  'tt',
  'td',
  'w',
  'h',
  'miw',
  'mih',
  'maw',
  'mah',
  'opacity',
  'bdrs',
  'bgsz',
  'bgp',
  'bgr',
  'bga',
  'pos',
  'top',
  'left',
  'bottom',
  'right',
  'inset',
  'display',
  'flex',
])

/** Separates style props from other component props, returning inline styles and the remaining element props. */
export function pickStyleProps(rest: Record<string, unknown>): {
  styleObj: StyleObject
  ownRest: Record<string, unknown>
} {
  const styleKeys: Record<string, unknown> = {}
  const ownRest: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(rest)) {
    if (STYLE_PROP_KEYS.has(k)) styleKeys[k] = v
    else ownRest[k] = v
  }
  return { styleObj: applyStyleProps(styleKeys as Partial<StyleProps>), ownRest }
}

/** Resolved CSS values for a component variant (background, hover state, text color, border). */
export interface VariantColorResult {
  /** CSS value for the default background. */
  background: string
  /** CSS value for the hover background. */
  hover: string
  /** CSS value for the foreground text color. */
  color: string
  /** CSS value for the border (includes width and style). */
  border: string
}

/** Returns the full set of CSS color values for a given theme color and component variant (filled, light, outline, etc.). */
export function resolveVariantColors(
  color: string | undefined,
  variant: string,
): VariantColorResult {
  const c = color ?? 'primary'

  switch (variant) {
    case 'filled':
      return {
        background: `var(--dv-color-${c}-filled)`,
        hover: `var(--dv-color-${c}-filled-hover)`,
        color: `var(--dv-color-${c}-filled-color)`,
        border: '0.0625rem solid transparent',
      }
    case 'light':
      return {
        background: `var(--dv-color-${c}-light)`,
        hover: `var(--dv-color-${c}-light-hover)`,
        color: `var(--dv-color-${c}-light-color)`,
        border: '0.0625rem solid transparent',
      }
    case 'outline':
      return {
        background: 'transparent',
        hover: `var(--dv-color-${c}-outline-hover)`,
        color: `var(--dv-color-${c}-outline)`,
        border: `0.0625rem solid var(--dv-color-${c}-outline)`,
      }
    case 'subtle':
      return {
        background: 'transparent',
        hover: `var(--dv-color-${c}-light-hover)`,
        color: `var(--dv-color-${c}-light-color)`,
        border: '0.0625rem solid transparent',
      }
    case 'transparent':
      return {
        background: 'transparent',
        hover: 'transparent',
        color: `var(--dv-color-${c}-light-color)`,
        border: '0.0625rem solid transparent',
      }
    case 'white':
      return {
        background: 'var(--dv-color-white)',
        hover: 'var(--dv-color-white)',
        color: `var(--dv-color-${c}-filled)`,
        border: '0.0625rem solid transparent',
      }
    default:
      return {
        background: 'var(--dv-color-default)',
        hover: 'var(--dv-color-default-hover)',
        color: 'var(--dv-color-default-color)',
        border: '0.0625rem solid var(--dv-color-default-border)',
      }
  }
}
